// @ts-nocheck
import { useState, useMemo, useCallback } from "react";

// ═════════════════════════════════════════════════════════════════════════════
// KY INTAKE SYSTEM — v4
// Builds on v3 (smart height parsing, calc safety rails, training time,
// meal prep support, social follow-up). All v4 changes marked "── v4".
//
//  HEALTH SCREENING (progressive disclosure — invisible unless relevant)
//  1. One Yes/No health question in Step 1. "No" costs two seconds.
//     "Yes" expands: conditions checklist, injuries, medications, plus two
//     PAR-Q-style safety questions (chest pain / doctor restriction).
//  2. Pregnancy / postpartum status — shown only for female clients.
//
//  GATES — two kinds, behaving differently:
//  3. HARD STOPS (rare, safety-only, never a dead-end):
//       · Age under 18            → friendly parent/coach-call redirect
//       · Pregnant or breastfeeding + deficit-strategy goal
//       · Chest pain during activity OR doctor-advised restriction
//     Hard stops replace the plan preview with a warm "let's build this
//     together properly" screen + booking link. The submission STILL sends
//     to the coaches with full flags — the lead is never lost.
//  4. SOFT FLAGS (everything else): client never blocked, never told.
//     Conditions/injuries/medications silently set coachReviewRequired and
//     appear itemized in the coach panel + systemWarnings payload field.
// ═════════════════════════════════════════════════════════════════════════════

// ── COLORS ──────────────────────────────────────────────────────────────────
const c = {
  gold: "#C9973A", goldLight: "#E8C06A", goldDark: "#8B6820",
  bg: "#080806", card: "#0E0E0A", border: "#2A2410", borderLight: "#3A3218",
  text: "#F0E0C8", soft: "#CFC3A0", muted: "#8A7A50", mutedDark: "#4A3E20",
  black: "#000",
  green: "#1E3D2A", greenText: "#6EE09A", greenBorder: "#2A5C3A",
  red: "#3D1E1E", redText: "#E07E7E", redBorder: "#5C2A2A",
  yellow: "#3A300A", yellowText: "#E8C06A", yellowBorder: "#5A4A1A",
};

// ── OPTION LISTS ─────────────────────────────────────────────────────────────
const GOALS = [
  { v: "fat_loss", l: "Lose body fat" },
  { v: "muscle_gain", l: "Build muscle and strength" },
  { v: "body_recomposition", l: "Reshape my body composition" },
  { v: "performance", l: "Improve athletic performance" },
  { v: "general_health", l: "Feel healthier and more energized" },
  { v: "maintenance", l: "Maintain what I have" },
];
const SEXES = [{ v: "male", l: "Male" }, { v: "female", l: "Female" }, { v: "prefer_not_to_say", l: "Prefer not to say" }];
const LIFESTYLE_LOAD = [
  { v: "low", l: "Low — relaxed schedule, minimal stress" },
  { v: "moderate", l: "Moderate — busy but manageable" },
  { v: "high", l: "High — demanding schedule, high stress" },
];
const ACTIVITY = [
  { v: "sedentary", l: "Sedentary — desk job, minimal movement" },
  { v: "lightly_active", l: "Lightly active — 1–2 workouts/wk" },
  { v: "moderately_active", l: "Moderately active — 3–4 workouts/wk" },
  { v: "very_active", l: "Very active — 5–6 workouts/wk" },
  { v: "extremely_active", l: "Extremely active — athlete or physical job" },
];
const TRAINING_EXP = [
  { v: "beginner", l: "Beginner — less than 6 months consistent training" },
  { v: "intermediate", l: "Intermediate — 6 months to 2 years" },
  { v: "advanced", l: "Advanced — 2+ years of structured programming" },
];
const AVAILABLE_DAYS = [
  { v: "2_days", l: "2 days" },
  { v: "3_days", l: "3 days" },
  { v: "4_days", l: "4 days" },
  { v: "5_days", l: "5 days" },
  { v: "6_plus_days", l: "6+ days" },
];
const TRAINING_TIME = [
  { v: "early_morning", l: "Early morning (5–8am)" },
  { v: "morning", l: "Morning (8–11am)" },
  { v: "midday", l: "Midday (11am–2pm)" },
  { v: "afternoon", l: "Afternoon (2–5pm)" },
  { v: "evening", l: "Evening (5–9pm)" },
  { v: "varies", l: "It varies week to week" },
];
const MEAL_PREP_SUPPORT = [
  { v: "self", l: "I prepare my own food" },
  { v: "partner_family", l: "A partner or family member cooks for me" },
  { v: "meal_service", l: "Meal prep service / delivery" },
  { v: "mostly_eat_out", l: "I mostly eat out or order in" },
  { v: "mixed", l: "A mix of the above" },
];
const SOCIAL_PATTERN = [
  { v: "eat_too_much", l: "I eat too much — portions get away from me" },
  { v: "quality_drops", l: "Quality drops — I still eat, just worse food" },
  { v: "skip_meals", l: "I skip meals or forget to eat" },
  { v: "drinks_add_up", l: "Drinks add up more than the food does" },
];

// ── v4 NEW: Health screening options ─────────────────────────────────────────
const YESNO = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const HEALTH_CONDITIONS = [
  { v: "heart_condition", l: "Heart condition" },
  { v: "high_blood_pressure", l: "High blood pressure" },
  { v: "diabetes", l: "Diabetes (Type 1 or 2)" },
  { v: "asthma_respiratory", l: "Asthma / respiratory" },
  { v: "joint_back_issues", l: "Joint, knee, or back issues" },
  { v: "recent_surgery", l: "Surgery in the last 12 months" },
  { v: "thyroid_hormonal", l: "Thyroid / hormonal condition" },
  { v: "other", l: "Something else" },
];
const PREGNANCY_OPTS = [
  { v: "none", l: "None of these apply" },
  { v: "pregnant", l: "Currently pregnant" },
  { v: "postpartum", l: "Postpartum (last 6 months)" },
  { v: "breastfeeding", l: "Currently breastfeeding" },
  { v: "trying", l: "Trying to conceive" },
];

const DIET_HISTORY = [
  { v: "never_dieted", l: "Never dieted — first structured attempt" },
  { v: "occasional_attempts", l: "Occasional attempts — tried a couple times" },
  { v: "frequent_dieter", l: "Frequent dieter — many attempts" },
  { v: "chronic_restrictive", l: "Chronic restriction — long-term low-calorie" },
  { v: "yo_yo_history", l: "Yo-yo pattern — repeated gain/loss cycles" },
];
const ENERGY_REC = [
  { v: "poor", l: "Poor — chronically fatigued, poor sleep" },
  { v: "fair", l: "Fair — inconsistent rest and energy" },
  { v: "good", l: "Good — generally rested and recovered" },
  { v: "excellent", l: "Excellent — high energy, consistent sleep" },
];
const FRICTION_OPTS = [
  { v: "time_constraints", l: "Not enough time" },
  { v: "emotional_eating", l: "Emotional eating" },
  { v: "social_pressure", l: "Social pressure" },
  { v: "inconsistent_schedule", l: "Inconsistent schedule" },
  { v: "food_environment", l: "Poor food environment" },
  { v: "financial_barriers", l: "Budget / cost" },
  { v: "knowledge_gaps", l: "Unsure what to eat" },
  { v: "motivation_fluctuation", l: "Motivation swings" },
  { v: "portion_control", l: "Portion control" },
  { v: "meal_prep_avoidance", l: "Avoiding meal prep" },
];
const CRITICAL_ZONES = [
  { v: "weekends", l: "Weekends" }, { v: "evenings", l: "Late evenings" },
  { v: "social_events", l: "Social events" }, { v: "travel", l: "Travel" },
  { v: "stress", l: "High-stress periods" }, { v: "work_schedule", l: "Work schedule" },
  { v: "none", l: "No specific zone" },
];
const OFF_TRACK = [
  { v: "binge_restrict", l: "Binge then restrict" },
  { v: "weekend_collapse", l: "Everything falls apart on weekends" },
  { v: "stress_eating", l: "Eat when stressed or emotional" },
  { v: "social_pressure", l: "Give in to social pressure" },
  { v: "skipping_meals", l: "Skip meals then overeat" },
  { v: "portion_distortion", l: "Lose track of portions" },
  { v: "none_identified", l: "No clear pattern yet" },
];
const CUISINE = [
  { v: "american", l: "American / Southern" }, { v: "latin", l: "Latin / Caribbean" },
  { v: "mediterranean", l: "Mediterranean" }, { v: "asian", l: "Asian" },
  { v: "mixed", l: "Mixed — I eat everything" }, { v: "no_preference", l: "No preference" },
];
const COOKING = [
  { v: "none", l: "I don't cook" }, { v: "basic", l: "Basic — simple meals" },
  { v: "intermediate", l: "Intermediate — comfortable" }, { v: "advanced", l: "Advanced — skilled cook" },
];
const MEAL_STRUCT = [
  { v: "3_meals", l: "3 meals a day" }, { v: "4_meals", l: "4 meals a day" },
  { v: "5_meals", l: "5 smaller meals / snacks" },
  { v: "intermittent_fasting", l: "Intermittent fasting" },
  { v: "grazing", l: "Grazing throughout the day" }, { v: "flexible", l: "No set structure" },
];
const ALLERGY_OPTS = [
  { v: "none", l: "None" }, { v: "gluten", l: "Gluten" }, { v: "dairy", l: "Dairy" },
  { v: "eggs", l: "Eggs" }, { v: "nuts", l: "Nuts" }, { v: "shellfish", l: "Shellfish" },
  { v: "soy", l: "Soy" }, { v: "fish", l: "Fish" },
];
const GOAL_URGENCY = [
  { v: "low", l: "Low — flexible timeline, no rush" },
  { v: "moderate", l: "Moderate — steady and committed" },
  { v: "high", l: "High — motivated and ready to push" },
  { v: "critical", l: "Critical — I need results now" },
];
const DATA_CONF = [
  { v: "measured_recently", l: "Measured recently — I know my numbers" },
  { v: "estimated", l: "Estimated — best guess" },
  { v: "not_sure", l: "Not sure — I'd need to verify" },
];
const WEEKDAY_PAT = [
  { v: "structured", l: "Structured — same routine daily" },
  { v: "variable", l: "Variable — changes each day" },
  { v: "busy_skips", l: "Busy — often skip meals" },
  { v: "meal_prep", l: "Prepped — eat from weekly prep" },
];
const WEEKEND_PAT = [
  { v: "similar_to_weekday", l: "Similar to weekdays" },
  { v: "more_relaxed", l: "More relaxed, occasional treats" },
  { v: "less_structured", l: "Less structured, unplanned" },
  { v: "social_eating", l: "Heavy social / restaurant eating" },
];
const COACHES = [{ v: "Kevin", l: "Kevin" }, { v: "Yuneisy", l: "Yuneisy" }, { v: "Both", l: "Both" }];
const ACCT_INTEREST = [
  { v: "high", l: "Full accountability support" },
  { v: "moderate", l: "Some check-ins would help" },
  { v: "independent", l: "I prefer to work independently" },
];

// ── RETURNING CLIENT TEMPLATE (production: replace with DB lookup) ─────────
const RETURNING_TEMPLATE = {
  sex: "female",
  knownAllergies: ["dairy"],
  cuisinePreference: "latin",
  foodsLiked: ["rice", "chicken", "avocado", "eggs"],
  foodsDisliked: ["broccoli"],
  cookingLevel: "intermediate",
};

const CALENDLY_URL = "https://calendly.com/kyempoweredfitness";

// ── UTILITY FUNCTIONS ────────────────────────────────────────────────────────
const toKg = (v, u) => !v ? null : u === "lb" ? parseFloat(v) * 0.453592 : parseFloat(v);
const toLb = (v, u) => !v ? null : u === "kg" ? parseFloat(v) * 2.205 : parseFloat(v);

// ── v3: Smart height parser ──────────────────────────────────────────────────
function parseHeightToInches(raw, unit) {
  if (raw === "" || raw == null) return { inches: null, display: "", assumed: null };
  const s = String(raw).trim();

  if (unit === "cm") {
    const n = parseFloat(s);
    if (isNaN(n) || n <= 0) return { inches: null, display: "", assumed: null };
    return { inches: n / 2.54, display: `${n} cm (${Math.round(n / 2.54)} in)`, assumed: "cm" };
  }

  const ftIn = s.match(/^(\d{1,2})\s*(?:'|ft|feet)\s*(\d{1,2})?\s*(?:"|in|inches)?\s*$/i);
  if (ftIn) {
    const ft = parseInt(ftIn[1], 10);
    const inch = ftIn[2] ? parseInt(ftIn[2], 10) : 0;
    if (inch > 11) return { inches: null, display: "invalid — inches must be 0–11", assumed: "invalid" };
    const total = ft * 12 + inch;
    return { inches: total, display: `${ft}'${inch}" = ${total} inches`, assumed: "feet_inches" };
  }

  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return { inches: null, display: "", assumed: null };

  if (n < 10) {
    const ft = Math.floor(n);
    const decRaw = s.includes(".") ? s.split(".")[1].replace(/\D/g, "") : "";
    let inch = decRaw ? parseInt(decRaw.slice(0, 2), 10) : 0;
    if (inch > 11) inch = 11;
    const total = ft * 12 + inch;
    return { inches: total, display: `read as ${ft}'${inch}" = ${total} inches`, assumed: "feet_decimal" };
  }

  return { inches: n, display: `${n} inches (${Math.floor(n / 12)}'${Math.round(n % 12)}")`, assumed: "inches" };
}

function computeBMR(age, sex, hCm, wKg) {
  if (!age || !sex || !hCm || !wKg) return null;
  const base = 10 * wKg + 6.25 * hCm - 5 * parseFloat(age);
  return Math.round(sex === "male" ? base + 5 : base - 161);
}

const ACTIVITY_MULT = { sedentary: 1.2, lightly_active: 1.375, moderately_active: 1.55, very_active: 1.725, extremely_active: 1.9 };
const GOAL_CAL_ADJ = { fat_loss: -500, muscle_gain: 300, body_recomposition: -200, performance: 150, general_health: -100, maintenance: 0 };
const PROTEIN_PER_LB = { fat_loss: 0.9, muscle_gain: 1.0, body_recomposition: 0.9, performance: 1.1, general_health: 0.7, maintenance: 0.7 };

const CAL_FLOOR = { male: 1500, female: 1200, prefer_not_to_say: 1200 };
const HEIGHT_MIN_IN = 54;
const HEIGHT_MAX_IN = 84;
const WEIGHT_MIN_LB = 80;
const WEIGHT_MAX_LB = 500;

function computeCalcs(form) {
  const { bodyData, goalProfile } = form;
  const hp = parseHeightToInches(bodyData.height.value, bodyData.height.unit);
  const heightIn = hp.inches;
  const hCm = heightIn ? heightIn * 2.54 : null;
  const wLb = toLb(bodyData.weight.value, bodyData.weight.unit);
  const wKg = toKg(bodyData.weight.value, bodyData.weight.unit);

  const bmr = computeBMR(bodyData.age, bodyData.sex, hCm, wKg);
  const mult = ACTIVITY_MULT[goalProfile.activityLevel] || 1.375;
  const tdee = bmr ? Math.round(bmr * mult) : null;
  const rawTarget = tdee != null ? Math.round(tdee + (GOAL_CAL_ADJ[goalProfile.primaryGoal] || 0)) : null;

  const floor = CAL_FLOOR[bodyData.sex] || 1200;
  const calorieFloorApplied = rawTarget != null && rawTarget < floor;
  const targetCals = rawTarget == null ? null : Math.max(rawTarget, floor);

  const protein = wLb ? Math.round(wLb * (PROTEIN_PER_LB[goalProfile.primaryGoal] || 0.8)) : null;

  const warnings = [];
  if (hp.assumed === "invalid") warnings.push("height entry invalid — could not parse");
  if (hp.assumed === "feet_decimal") warnings.push(`height auto-interpreted (${hp.display}) — confirm with client`);
  if (heightIn != null && (heightIn < HEIGHT_MIN_IN || heightIn > HEIGHT_MAX_IN))
    warnings.push(`height ${Math.round(heightIn)} in is outside the expected adult range — verify before building plan`);
  if (wLb != null && (wLb < WEIGHT_MIN_LB || wLb > WEIGHT_MAX_LB))
    warnings.push(`weight ${Math.round(wLb)} lb is outside the expected range — verify`);
  if (calorieFloorApplied)
    warnings.push(`raw calorie target (${rawTarget?.toLocaleString()}) fell below the ${floor.toLocaleString()} safety floor — output clamped, inputs likely wrong`);
  if (protein != null && targetCals != null && protein * 4 > targetCals * 0.65)
    warnings.push(`protein target (${protein}g = ${protein * 4} cal) doesn't fit the calorie budget — inputs are inconsistent`);

  return { heightIn, heightDisplay: hp.display, heightAssumed: hp.assumed, bmr, tdee, rawTarget, targetCals, calorieFloorApplied, protein, warnings };
}

function normalizePhone(p) {
  if (!p) return "";
  const digits = String(p).replace(/\D/g, "");
  const d = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (d.length === 10) return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

function deriveAdherenceRisk(score) {
  if (score == null) return "";
  return score >= 8 ? "low" : score >= 5 ? "moderate" : "high";
}
function derivePlanIntensity(urgency, adherenceRisk) {
  if (urgency === "critical" || urgency === "high") return adherenceRisk === "high" ? "moderate" : "aggressive";
  if (urgency === "moderate") return "moderate";
  return "conservative";
}
function derivePrimaryDriver(fps) {
  if (!fps?.length) return "nutrition";
  const behav = ["emotional_eating", "motivation_fluctuation", "social_pressure", "meal_prep_avoidance"];
  return fps.some(f => behav.includes(f)) ? "behavior" : "nutrition";
}
function deriveNutritionStrategy(goal) {
  const m = { fat_loss: "caloric_deficit", muscle_gain: "caloric_surplus", body_recomposition: "caloric_deficit", performance: "meal_timing", general_health: "maintenance", maintenance: "maintenance" };
  return m[goal] || "maintenance";
}
function isUnreasonableBF(pct, sex) {
  if (!pct || isNaN(parseFloat(pct))) return false;
  const n = parseFloat(pct);
  if (n < 2 || n > 60) return true;
  if (sex === "male" && (n < 3 || n > 45)) return true;
  if (sex === "female" && (n < 10 || n > 55)) return true;
  return false;
}
function needsSocialFollowUp(form) {
  return form.frictionProfile.criticalZone === "social_events" ||
    form.frictionProfile.offTrackPattern === "social_pressure" ||
    form.frictionProfile.frictionPoints.includes("social_pressure");
}

// ── v4: age gate helper ───────────────────────────────────────────────────────
function isMinor(age) {
  const n = parseInt(age, 10);
  return !isNaN(n) && n > 0 && n < 18;
}

// ── v4: HARD STOPS — the only gates that change the client's path.
// Returns [] for the vast majority of visitors. Each entry has a machine
// reason (for the payload) and nothing client-facing — the SafetyBridge
// screen handles the human side warmly.
function computeHardStops(form, derived) {
  const stops = [];
  const h = form.healthProfile;
  if (isMinor(form.bodyData.age)) stops.push("under_18");
  const deficit = ["caloric_deficit"].includes(derived.nutritionStrategyInput);
  if ((h.pregnancyStatus === "pregnant" || h.pregnancyStatus === "breastfeeding") && deficit)
    stops.push("pregnancy_deficit");
  if (h.hasHealthFlags === "yes" && h.chestPain === "yes") stops.push("chest_pain");
  if (h.hasHealthFlags === "yes" && h.doctorRestriction === "yes") stops.push("doctor_restriction");
  return stops;
}

// ── v4: SOFT health flags — never block, always inform the coach ─────────────
function computeHealthWarnings(form) {
  const h = form.healthProfile;
  const w = [];
  if (h.hasHealthFlags === "yes") {
    if (h.conditions.length) w.push(`health conditions disclosed: ${h.conditions.join(", ")}`);
    if (h.injuriesNotes?.trim()) w.push(`injuries noted: "${h.injuriesNotes.trim().slice(0, 120)}"`);
    if (h.medicationsNotes?.trim()) w.push(`medications noted: "${h.medicationsNotes.trim().slice(0, 120)}"`);
  }
  if (h.pregnancyStatus && !["", "none"].includes(h.pregnancyStatus))
    w.push(`pregnancy/postpartum status: ${h.pregnancyStatus}`);
  return w;
}

function deriveFields(form) {
  const adherenceRisk = deriveAdherenceRisk(form.clientProfile.consistencyScore);
  const planIntensityInput = derivePlanIntensity(form.goalProfile.goalUrgency, adherenceRisk);
  const primaryDriverInput = derivePrimaryDriver(form.frictionProfile.frictionPoints);
  const nutritionStrategyInput = deriveNutritionStrategy(form.goalProfile.primaryGoal);
  const riskWindows = form.frictionProfile.criticalZone && form.frictionProfile.criticalZone !== "none"
    ? [form.frictionProfile.criticalZone] : [];
  return { adherenceRisk, planIntensityInput, primaryDriverInput, nutritionStrategyInput, riskWindows };
}

function computeFlags(form, derived, calcs) {
  const { bodyData, goalProfile, clientProfile } = form;
  const metricsReady = Boolean(bodyData.age && bodyData.sex && calcs.heightIn && bodyData.weight.value);
  const ar = derived.adherenceRisk;
  const fatLossPaceEligible = goalProfile.primaryGoal === "fat_loss" &&
    ["low", "moderate"].includes(ar) && ["low", "moderate"].includes(clientProfile.lifestyleLoad) &&
    clientProfile.consistencyScore >= 6;
  const healthWarnings = computeHealthWarnings(form); // v4
  const coachReviewRequired = bodyData.dataConfidence === "not_sure" || !metricsReady ||
    (derived.planIntensityInput === "aggressive" && ar === "high") ||
    isUnreasonableBF(bodyData.bodyFatPercent, bodyData.sex) ||
    calcs.warnings.length > 0 ||
    healthWarnings.length > 0; // v4: any health disclosure = coach review
  return { metricsReady, fatLossPaceEligible, coachReviewRequired, healthWarnings };
}

function computeConfidence(form, flags, calcs) {
  const fields = [form.bodyData.age, form.bodyData.sex, form.bodyData.height.value, form.bodyData.weight.value,
    form.goalProfile.primaryGoal, form.goalProfile.activityLevel, form.clientProfile.lifestyleLoad,
    form.nutritionContext.cuisinePreference, form.nutritionContext.cookingLevel, form.frictionProfile.frictionPoints.length > 0 ? "y" : "",
    form.goalProfile.trainingExperience, form.goalProfile.availableDaysPerWeek,
    form.goalProfile.trainingTimeOfDay, form.nutritionContext.mealPrepSupport,
    form.healthProfile.hasHealthFlags, // v4
  ];
  const pct = fields.filter(Boolean).length / fields.length;
  if (!flags.metricsReady || form.bodyData.dataConfidence === "not_sure") return "low";
  if (calcs.warnings.length > 0) return "low";
  if (pct >= 0.9 && !flags.coachReviewRequired) return "high";
  if (pct >= 0.6) return "medium";
  return "low";
}

function computeCompletion(form) {
  const all = [form.clientInfo.name, form.bodyData.age, form.bodyData.sex, form.bodyData.height.value,
    form.bodyData.weight.value, form.goalProfile.primaryGoal, form.goalProfile.activityLevel,
    form.goalProfile.dietHistory, form.clientProfile.lifestyleLoad, form.clientProfile.energyRecovery,
    form.frictionProfile.frictionPoints.length > 0 ? "y" : "", form.frictionProfile.criticalZone,
    form.frictionProfile.offTrackPattern, form.nutritionContext.cuisinePreference,
    form.nutritionContext.cookingLevel, form.nutritionContext.mealStructurePreference,
    form.goalProfile.goalUrgency, form.bodyData.dataConfidence,
    form.goalProfile.trainingExperience, form.goalProfile.availableDaysPerWeek,
    form.goalProfile.trainingTimeOfDay, form.nutritionContext.mealPrepSupport,
    form.healthProfile.hasHealthFlags, // v4
  ];
  return Math.round((all.filter(Boolean).length / all.length) * 100);
}

function buildJSON(form, derived, flags, confidence, calcs, hardStops) {
  return {
    clientInfo: { name: form.clientInfo.name, email: form.clientInfo.email, phone: normalizePhone(form.clientInfo.phone), date: form.clientInfo.date, coach: form.clientInfo.coach },
    bodyData: {
      age: form.bodyData.age ? parseInt(form.bodyData.age) : null,
      sex: form.bodyData.sex,
      height: { value: form.bodyData.height.value || null, unit: form.bodyData.height.unit },
      heightNormalizedIn: calcs.heightIn ? Math.round(calcs.heightIn) : null,
      heightInterpretation: calcs.heightDisplay || null,
      weight: { value: form.bodyData.weight.value ? parseFloat(form.bodyData.weight.value) : null, unit: form.bodyData.weight.unit },
      bodyFatPercent: form.bodyData.bodyFatPercent ? parseFloat(form.bodyData.bodyFatPercent) : null,
      dataConfidence: form.bodyData.dataConfidence,
    },
    healthProfile: { // v4
      hasHealthFlags: form.healthProfile.hasHealthFlags,
      conditions: form.healthProfile.conditions,
      injuriesNotes: form.healthProfile.injuriesNotes || null,
      medicationsNotes: form.healthProfile.medicationsNotes || null,
      chestPain: form.healthProfile.chestPain || null,
      doctorRestriction: form.healthProfile.doctorRestriction || null,
      pregnancyStatus: form.healthProfile.pregnancyStatus || null,
    },
    goalProfile: {
      primaryGoal: form.goalProfile.primaryGoal,
      goalUrgency: form.goalProfile.goalUrgency,
      activityLevel: form.goalProfile.activityLevel,
      dietHistory: form.goalProfile.dietHistory,
      trainingExperience: form.goalProfile.trainingExperience,
      availableDaysPerWeek: form.goalProfile.availableDaysPerWeek,
      trainingTimeOfDay: form.goalProfile.trainingTimeOfDay,
    },
    clientProfile: {
      adherenceRisk: derived.adherenceRisk, planIntensityInput: derived.planIntensityInput,
      lifestyleLoad: form.clientProfile.lifestyleLoad, primaryDriverInput: derived.primaryDriverInput,
      nutritionStrategyInput: derived.nutritionStrategyInput, energyRecovery: form.clientProfile.energyRecovery,
      consistencyScore: form.clientProfile.consistencyScore,
    },
    frictionProfile: {
      frictionPoints: form.frictionProfile.frictionPoints, criticalZone: form.frictionProfile.criticalZone,
      offTrackPattern: form.frictionProfile.offTrackPattern, riskWindows: derived.riskWindows,
      socialEventPattern: form.frictionProfile.socialEventPattern || null,
    },
    nutritionContext: {
      cuisinePreference: form.nutritionContext.cuisinePreference, cookingLevel: form.nutritionContext.cookingLevel,
      mealPrepSupport: form.nutritionContext.mealPrepSupport,
      mealStructurePreference: form.nutritionContext.mealStructurePreference, knownAllergies: form.nutritionContext.knownAllergies,
      foodsLiked: form.nutritionContext.foodsLiked, foodsDisliked: form.nutritionContext.foodsDisliked,
      weekdayPattern: form.nutritionContext.weekdayPattern, weekendPattern: form.nutritionContext.weekendPattern,
    },
    calculations: {
      bmr: calcs.bmr, maintenanceCalories: calcs.tdee,
      rawTargetCalories: calcs.rawTarget, targetCalories: calcs.targetCals,
      calorieFloorApplied: calcs.calorieFloorApplied, proteinTarget: calcs.protein,
    },
    systemFlags: {
      metricsReady: flags.metricsReady, fatLossPaceEligible: flags.fatLossPaceEligible,
      coachReviewRequired: flags.coachReviewRequired, systemConfidence: confidence,
      systemWarnings: calcs.warnings,
      healthWarnings: flags.healthWarnings, // v4
      hardStops, // v4
    },
  };
}

function generatePreview(form, derived, flags, calcs) {
  const { goalProfile, clientProfile, frictionProfile, nutritionContext } = form;
  const { tdee, targetCals, protein, bmr } = calcs;
  const firstName = form.clientInfo.name ? form.clientInfo.name.split(" ")[0] : "there";
  const GOAL_LABELS = { fat_loss: "sustainable fat loss", muscle_gain: "building lean muscle", body_recomposition: "reshaping your composition", performance: "elevating your performance", general_health: "building a healthier lifestyle", maintenance: "maintaining your foundation" };
  const URGENCY_STYLE = { low: "patient and sustainable", moderate: "steady and committed", high: "focused and driven", critical: "intense and accelerated" };
  const ar = derived.adherenceRisk;
  const adherencePred = ar === "low" ? "Strong — your track record shows you follow through."
    : ar === "moderate" ? "Solid — with the right structure, consistency is within reach."
    : "Needs support — we'll build accountability layers into your plan.";
  const paceClass = flags.fatLossPaceEligible ? "Accelerated fat loss — eligible based on your full profile"
    : derived.planIntensityInput === "conservative" ? "Conservative progression — steady, sustainable wins"
    : "Moderate, structured pace — right for where you are now";
  const firstAction = goalProfile.primaryGoal === "fat_loss" ? `Hit your daily protein target of ${protein || "—"}g. Everything else follows from that.`
    : goalProfile.primaryGoal === "muscle_gain" ? `Track protein daily — aim for ${protein || "—"}g and hit every session.`
    : `Track your current intake for 3 days without changing anything. Data first.`;
  const trainingNote = clientProfile.lifestyleLoad === "high"
    ? "We'll match training intensity to your recovery capacity. Quality over volume, always."
    : ["sedentary", "lightly_active"].includes(goalProfile.activityLevel)
    ? "We'll build your training gradually and sustainably — no shock, no crash."
    : "Your current activity level is a real asset. We'll build on what you already have.";
  const cuisineLabel = CUISINE.find(x => x.v === nutritionContext.cuisinePreference)?.l || "";
  const frictionCount = frictionProfile.frictionPoints.length;
  const frictionLabels = frictionProfile.frictionPoints.slice(0, 2).map(f => FRICTION_OPTS.find(o => o.v === f)?.l?.toLowerCase() || f);
  return { firstName, goalLabel: GOAL_LABELS[goalProfile.primaryGoal] || "reaching your goals", paceStyle: URGENCY_STYLE[goalProfile.goalUrgency] || "balanced", bmr, tdee, targetCals, protein, adherencePred, paceClass, firstAction, trainingNote, cuisineLabel, frictionCount, frictionLabels };
}

// ── STEP VALIDATION ──────────────────────────────────────────────────────────
function isStepComplete(step, form, calcs) {
  const f = form;
  const h = f.healthProfile;
  switch (step) {
    case 1: {
      const base = Boolean(f.clientInfo.name && f.clientInfo.email && f.bodyData.age && f.bodyData.sex && calcs.heightIn && f.bodyData.weight.value && f.goalProfile.primaryGoal);
      // ── v4: age gate — minors cannot advance (friendly notice shows inline)
      if (isMinor(f.bodyData.age)) return false;
      // ── v4: health screen must be answered; "yes" expands two safety Qs
      const healthAnswered = h.hasHealthFlags === "no" ||
        (h.hasHealthFlags === "yes" && h.chestPain && h.doctorRestriction);
      // ── v4: pregnancy status required for female clients
      const pregAnswered = f.bodyData.sex !== "female" || Boolean(h.pregnancyStatus);
      return base && healthAnswered && pregAnswered;
    }
    case 2: return Boolean(f.clientProfile.lifestyleLoad && f.goalProfile.activityLevel && f.goalProfile.dietHistory && f.goalProfile.trainingExperience && f.goalProfile.availableDaysPerWeek && f.goalProfile.trainingTimeOfDay);
    case 3: return Boolean(f.frictionProfile.frictionPoints.length > 0 && f.frictionProfile.criticalZone && f.frictionProfile.offTrackPattern && (!needsSocialFollowUp(f) || f.frictionProfile.socialEventPattern));
    case 4: return Boolean(f.nutritionContext.cuisinePreference && f.nutritionContext.cookingLevel && f.nutritionContext.mealStructurePreference && f.nutritionContext.mealPrepSupport);
    case 5: return Boolean(f.goalProfile.goalUrgency && f.bodyData.dataConfidence);
    default: return true;
  }
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const INP = { width: "100%", background: "#0A0A08", color: c.text, border: `1px solid ${c.border}`, borderRadius: 8, padding: "12px 14px", fontFamily: "Georgia, serif", fontSize: 14, outline: "none", boxSizing: "border-box" };

// ── UI COMPONENTS ────────────────────────────────────────────────────────────
function FL({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: "block", color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 }}>{label}</label>
      {hint && <p style={{ color: c.mutedDark, fontSize: 11.5, marginBottom: 7, lineHeight: 1.5, margin: "0 0 7px" }}>{hint}</p>}
      {children}
    </div>
  );
}

function SF({ label, hint, value, onChange, options, placeholder = "Select..." }) {
  return (
    <FL label={label} hint={hint}>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...INP, cursor: "pointer" }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </FL>
  );
}

function TF({ label, hint, value, onChange, placeholder, type = "text" }) {
  return (
    <FL label={label} hint={hint}>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={INP} />
    </FL>
  );
}

function TA({ label, hint, value, onChange, placeholder }) {
  return (
    <FL label={label} hint={hint}>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} style={{ ...INP, resize: "vertical", minHeight: 52 }} />
    </FL>
  );
}

function NU({ label, hint, value, unit, onV, onU, units, placeholder }) {
  return (
    <FL label={label} hint={hint}>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="number" value={value} onChange={e => onV(e.target.value)} placeholder={placeholder} style={{ ...INP, flex: 1 }} />
        <select value={unit} onChange={e => onU(e.target.value)} style={{ ...INP, width: 68, cursor: "pointer", flex: "none", padding: "12px 8px" }}>
          {units.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
    </FL>
  );
}

function HeightField({ value, unit, onV, onU }) {
  const parsed = parseHeightToInches(value, unit);
  const outOfRange = parsed.inches != null && (parsed.inches < HEIGHT_MIN_IN || parsed.inches > HEIGHT_MAX_IN);
  const showConfirm = Boolean(value) && parsed.inches != null;
  const invalid = Boolean(value) && parsed.assumed === "invalid";
  return (
    <FL label="Height *" hint={unit === "in" ? `Type it however feels natural — 5'11", 5 ft 11, or 71` : "Centimeters"}>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={e => onV(e.target.value)}
          placeholder={unit === "in" ? `e.g. 5'11"` : "e.g. 170"}
          style={{ ...INP, flex: 1 }}
        />
        <select value={unit} onChange={e => onU(e.target.value)} style={{ ...INP, width: 68, cursor: "pointer", flex: "none", padding: "12px 8px" }}>
          {["in", "cm"].map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      {showConfirm && (
        <p style={{ color: outOfRange ? c.redText : c.greenText, fontSize: 12, margin: "7px 0 0", lineHeight: 1.5 }}>
          {outOfRange ? "⚠ " : "✓ "}{parsed.display}{outOfRange ? " — that seems unusual, double-check?" : ""}
        </p>
      )}
      {invalid && (
        <p style={{ color: c.redText, fontSize: 12, margin: "7px 0 0" }}>⚠ Couldn't read that — try a format like 5'11" or 71</p>
      )}
    </FL>
  );
}

function SinglePill({ label, hint, value, onChange, options }) {
  return (
    <FL label={label} hint={hint}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(o => {
          const on = value === o.v;
          return (
            <button key={o.v} onClick={() => onChange(o.v)} style={{
              background: on ? `${c.goldDark}33` : "#0A0A08",
              border: `1px solid ${on ? c.gold : c.border}`,
              color: on ? c.goldLight : c.muted,
              borderRadius: 8,
              padding: "10px 16px",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "Georgia, serif",
              transition: "all .15s",
              lineHeight: 1.4,
              textAlign: "left",
            }}>{o.l}</button>
          );
        })}
      </div>
    </FL>
  );
}

function MultiPill({ label, hint, selected, onChange, options }) {
  const toggle = v => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <FL label={label} hint={hint}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(o => {
          const on = selected.includes(o.v);
          return (
            <button key={o.v} onClick={() => toggle(o.v)} style={{
              background: on ? `${c.goldDark}33` : "#0A0A08",
              border: `1px solid ${on ? c.gold : c.border}`,
              color: on ? c.goldLight : c.muted,
              borderRadius: 999,
              padding: "8px 16px",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "Georgia, serif",
              transition: "all .15s",
            }}>{o.l}</button>
          );
        })}
      </div>
    </FL>
  );
}

function TagIn({ label, hint, tags, onChange, placeholder = "Add item, press Enter" }) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v && !tags.includes(v)) onChange([...tags, v]); setDraft(""); };
  const rem = v => onChange(tags.filter(t => t !== v));
  return (
    <FL label={label} hint={hint}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} style={{ ...INP, flex: 1, fontSize: 13 }} />
        <button onClick={add} style={{ background: `${c.goldDark}44`, border: `1px solid ${c.goldDark}`, color: c.gold, borderRadius: 8, padding: "0 16px", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 16 }}>+</button>
      </div>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {tags.map(t => (
            <span key={t} style={{ background: `${c.goldDark}22`, border: `1px solid ${c.goldDark}55`, color: c.soft, borderRadius: 999, padding: "5px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 7 }}>
              {t}<button onClick={() => rem(t)} style={{ background: "none", border: "none", color: c.muted, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </FL>
  );
}

function ScoreSlider({ label, hint, value, onChange }) {
  const pct = (value / 10) * 100;
  const col = value >= 7 ? c.greenText : value >= 4 ? c.goldLight : c.redText;
  return (
    <FL label={label} hint={hint}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
        <div style={{ flex: 1, position: "relative", height: 6, background: "#1A1A12", borderRadius: 3 }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${c.goldDark}, ${c.gold})`, borderRadius: 3, transition: "width .1s" }} />
          <input type="range" min={0} max={10} step={1} value={value} onChange={e => onChange(parseInt(e.target.value))} style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", cursor: "pointer" }} />
        </div>
        <span style={{ color: col, fontSize: 24, fontWeight: 700, minWidth: 30, textAlign: "right", fontFamily: "Georgia, serif" }}>{value}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: c.mutedDark, fontSize: 11 }}>0 — never consistent</span>
        <span style={{ color: c.mutedDark, fontSize: 11 }}>10 — always consistent</span>
      </div>
    </FL>
  );
}

function ConfBadge({ label, active, col, textCol }) {
  return (
    <div style={{ background: active ? col : "#141410", border: `1px solid ${active ? textCol + "44" : c.border}`, borderRadius: 7, padding: "6px 12px", display: "flex", alignItems: "center", gap: 7 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: active ? textCol : c.mutedDark, flexShrink: 0 }} />
      <span style={{ color: active ? textCol : c.muted, fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
    </div>
  );
}

// ── STEP COMPONENTS ──────────────────────────────────────────────────────────
function Step1({ form, set, setN }) {
  const h = form.healthProfile;
  const minor = isMinor(form.bodyData.age);
  return (
    <div>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
        Let's start with the essentials. This helps us build something that's actually designed around you.
      </p>
      <TF label="Your Name *" value={form.clientInfo.name} onChange={v => set("clientInfo", "name", v)} placeholder="Full name" />
      <TF label="Email Address *" value={form.clientInfo.email} onChange={v => set("clientInfo", "email", v)} placeholder="you@email.com" type="email" />
      <TF label="Phone Number" value={form.clientInfo.phone} onChange={v => set("clientInfo", "phone", v)} placeholder="+1 (305) 000-0000" type="tel" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 16 }}>
        <TF label="Age *" value={form.bodyData.age} onChange={v => set("bodyData", "age", v)} type="number" placeholder="e.g. 32" />
        <SF label="Sex *" value={form.bodyData.sex} onChange={v => set("bodyData", "sex", v)} options={SEXES} />
      </div>

      {/* ── v4: age gate — friendly, never a dead-end ── */}
      {minor && (
        <div style={{ background: c.yellow, border: `1px solid ${c.yellowBorder}`, borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
          <p style={{ color: c.yellowText, fontSize: 14, margin: "0 0 8px", fontWeight: 700 }}>We'd love to work with you — the right way.</p>
          <p style={{ color: c.soft, fontSize: 13, margin: 0, lineHeight: 1.7 }}>
            For anyone under 18, we build plans together with a parent or guardian involved — it's how we make sure everything is safe and right for a growing body. <a href={CALENDLY_URL} target="_blank" rel="noreferrer" style={{ color: c.goldLight, textDecoration: "underline" }}>Book a free call with Kevin or Yuny</a> and bring a parent along. We'll take it from there.
          </p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 16 }}>
        <HeightField
          value={form.bodyData.height.value}
          unit={form.bodyData.height.unit}
          onV={v => setN("bodyData","height","value",v)}
          onU={v => setN("bodyData","height","unit",v)}
        />
        <NU label="Weight *" value={form.bodyData.weight.value} unit={form.bodyData.weight.unit} onV={v => setN("bodyData","weight","value",v)} onU={v => setN("bodyData","weight","unit",v)} units={["lb","kg"]} placeholder="e.g. 185" />
      </div>
      <SinglePill label="What's your primary goal? *" value={form.goalProfile.primaryGoal} onChange={v => set("goalProfile","primaryGoal",v)} options={GOALS} />

      {/* ── v4: HEALTH SCREEN — one question, expands only on Yes ── */}
      <div style={{ height: 1, background: c.border, margin: "28px 0 24px" }} />
      <SinglePill
        label="Any injuries, medical conditions, or medications we should know about? *"
        hint="This helps us build a plan that's safe for your body — most people answer no and move right along."
        value={h.hasHealthFlags}
        onChange={v => set("healthProfile","hasHealthFlags",v)}
        options={YESNO}
      />

      {h.hasHealthFlags === "yes" && (
        <div style={{ background: "#0A0A07", border: `1px solid ${c.border}`, borderRadius: 12, padding: "20px 20px 4px", marginBottom: 20 }}>
          <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 16px" }}>
            Thanks for telling us — a few quick details
          </p>
          <MultiPill
            label="Which of these apply? (Select all that do)"
            selected={h.conditions}
            onChange={v => set("healthProfile","conditions",v)}
            options={HEALTH_CONDITIONS}
          />
          <TA
            label="Injuries or physical limitations"
            hint="Anything we should train around — old knee injury, shoulder issues, lower back, etc."
            value={h.injuriesNotes}
            onChange={v => set("healthProfile","injuriesNotes",v)}
            placeholder="e.g. torn ACL 2019, avoid deep squats"
          />
          <TA
            label="Medications (optional)"
            hint="Only if you're comfortable sharing — some medications affect energy, appetite, or heart rate during training."
            value={h.medicationsNotes}
            onChange={v => set("healthProfile","medicationsNotes",v)}
            placeholder="e.g. blood pressure medication"
          />
          <SinglePill
            label="Do you ever feel chest pain during physical activity? *"
            value={h.chestPain}
            onChange={v => set("healthProfile","chestPain",v)}
            options={YESNO}
          />
          <SinglePill
            label="Has a doctor ever told you to avoid or limit physical activity? *"
            value={h.doctorRestriction}
            onChange={v => set("healthProfile","doctorRestriction",v)}
            options={YESNO}
          />
        </div>
      )}

      {/* ── v4: pregnancy/postpartum — female clients only ── */}
      {form.bodyData.sex === "female" && (
        <SinglePill
          label="Do any of these currently apply to you? *"
          hint="Pregnancy, postpartum, and breastfeeding change what safe nutrition looks like — we build for it, not around it."
          value={h.pregnancyStatus}
          onChange={v => set("healthProfile","pregnancyStatus",v)}
          options={PREGNANCY_OPTS}
        />
      )}
    </div>
  );
}

function Step2({ form, set }) {
  return (
    <div>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
        Honest answers lead to a plan that actually works. We're building for your real life — not the week you wish you had.
      </p>
      <SinglePill label="Your current lifestyle load *" value={form.clientProfile.lifestyleLoad} onChange={v => set("clientProfile","lifestyleLoad",v)} options={LIFESTYLE_LOAD} />
      <SinglePill label="Current activity level *" value={form.goalProfile.activityLevel} onChange={v => set("goalProfile","activityLevel",v)} options={ACTIVITY} />
      <SF label="Diet history *" value={form.goalProfile.dietHistory} onChange={v => set("goalProfile","dietHistory",v)} options={DIET_HISTORY} hint="Be honest — this calibrates expectations and the approach." />
      <SinglePill label="How's your current energy and recovery?" value={form.clientProfile.energyRecovery} onChange={v => set("clientProfile","energyRecovery",v)} options={ENERGY_REC} />

      <div style={{ height: 1, background: c.border, margin: "28px 0 24px" }} />
      <p style={{ color: c.muted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>
        Training Profile
      </p>
      <SinglePill
        label="Training experience *"
        hint="How long have you been training consistently? Be honest — this shapes your entire program."
        value={form.goalProfile.trainingExperience}
        onChange={v => set("goalProfile","trainingExperience",v)}
        options={TRAINING_EXP}
      />
      <SinglePill
        label="How many days per week can you realistically train? *"
        hint="Not your ideal week — your real week. This determines your split and session structure."
        value={form.goalProfile.availableDaysPerWeek}
        onChange={v => set("goalProfile","availableDaysPerWeek",v)}
        options={AVAILABLE_DAYS}
      />
      <SinglePill
        label="What time of day do you usually train (or plan to)? *"
        hint="Your meal timing gets built around your session. A 7am workout and a 7pm workout produce very different plans."
        value={form.goalProfile.trainingTimeOfDay}
        onChange={v => set("goalProfile","trainingTimeOfDay",v)}
        options={TRAINING_TIME}
      />
    </div>
  );
}

function Step3({ form, set }) {
  const showSocial = needsSocialFollowUp(form);
  return (
    <div>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
        Real coaching starts here. The more honest you are, the better we can build around your patterns — not against them.
      </p>
      <MultiPill label="What usually gets in your way? (Select all that apply) *" hint="No wrong answers — this is your friction profile." selected={form.frictionProfile.frictionPoints} onChange={v => set("frictionProfile","frictionPoints",v)} options={FRICTION_OPTS} />
      <SinglePill label="Where do you most often fall off track? *" value={form.frictionProfile.criticalZone} onChange={v => set("frictionProfile","criticalZone",v)} options={CRITICAL_ZONES} />
      <SF label="When things go off-track, what does it look like? *" value={form.frictionProfile.offTrackPattern} onChange={v => set("frictionProfile","offTrackPattern",v)} options={OFF_TRACK} />

      {showSocial && (
        <SinglePill
          label="When social situations affect your eating, what usually happens? *"
          hint="This tells us exactly what to build for — the fix for overeating is completely different from the fix for skipped meals or quality drift."
          value={form.frictionProfile.socialEventPattern}
          onChange={v => set("frictionProfile","socialEventPattern",v)}
          options={SOCIAL_PATTERN}
        />
      )}

      <ScoreSlider label="Past consistency — how often have you followed through on health goals? (0–10)" hint="0 = never followed through   10 = always consistent" value={form.clientProfile.consistencyScore} onChange={v => set("clientProfile","consistencyScore",v)} />
    </div>
  );
}

function Step4({ form, set }) {
  const handleAllergies = (next) => {
    const prev = form.nutritionContext.knownAllergies;
    const added = next.find(v => !prev.includes(v));
    let cleaned = next;
    if (added === "none") cleaned = ["none"];
    else if (added) cleaned = next.filter(v => v !== "none");
    set("nutritionContext", "knownAllergies", cleaned);
  };
  return (
    <div>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
        Your nutrition plan should work with your actual life — the food you love, how you cook, and how you eat day to day.
      </p>
      <SinglePill label="Cuisine background *" value={form.nutritionContext.cuisinePreference} onChange={v => set("nutritionContext","cuisinePreference",v)} options={CUISINE} />
      <SinglePill label="Cooking level *" value={form.nutritionContext.cookingLevel} onChange={v => set("nutritionContext","cookingLevel",v)} options={COOKING} />
      <SinglePill
        label="Who usually prepares your food? *"
        hint="This matters more than cooking skill. Someone who can't cook but has a partner who preps eats very differently than someone assembling their own meals."
        value={form.nutritionContext.mealPrepSupport}
        onChange={v => set("nutritionContext","mealPrepSupport",v)}
        options={MEAL_PREP_SUPPORT}
      />
      <SinglePill label="Preferred meal structure *" value={form.nutritionContext.mealStructurePreference} onChange={v => set("nutritionContext","mealStructurePreference",v)} options={MEAL_STRUCT} />
      <MultiPill label="Allergies or intolerances" selected={form.nutritionContext.knownAllergies} onChange={handleAllergies} options={ALLERGY_OPTS} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
        <TagIn
          label="Foods you love"
          hint="The more you add, the more personal your meal plan gets — e.g. steak, rice, shrimp, avocado, pasta"
          tags={form.nutritionContext.foodsLiked}
          onChange={v => set("nutritionContext","foodsLiked",v)}
          placeholder="Add a food, press Enter"
        />
        <TagIn
          label="Foods you avoid or dislike"
          hint="Anything you never want to see on a plan — e.g. cucumber, eggplant, cilantro"
          tags={form.nutritionContext.foodsDisliked}
          onChange={v => set("nutritionContext","foodsDisliked",v)}
          placeholder="Add a food, press Enter"
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16 }}>
        <SF label="Weekday eating pattern" value={form.nutritionContext.weekdayPattern} onChange={v => set("nutritionContext","weekdayPattern",v)} options={WEEKDAY_PAT} />
        <SF label="Weekend eating pattern" value={form.nutritionContext.weekendPattern} onChange={v => set("nutritionContext","weekendPattern",v)} options={WEEKEND_PAT} />
      </div>
    </div>
  );
}

function Step5({ form, set, flags, confidence, isPublic = true }) {
  return (
    <div>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
        Almost done. Two final questions, then we generate your personalized KY plan.
      </p>
      <SinglePill label="How urgently do you want to see results? *" value={form.goalProfile.goalUrgency} onChange={v => set("goalProfile","goalUrgency",v)} options={GOAL_URGENCY} />
      <SF label="How confident are you in your measurements? *" value={form.bodyData.dataConfidence} onChange={v => set("bodyData","dataConfidence",v)} options={DATA_CONF} hint="Helps us personalize your starting point — best guess is perfectly fine." />

      {form.bodyData.dataConfidence === "not_sure" && (
        <div style={{ background: c.yellow, border: `1px solid ${c.yellowBorder}`, borderRadius: 10, padding: "14px 18px", marginTop: -8, marginBottom: 20 }}>
          <p style={{ color: c.yellowText, fontSize: 13, margin: 0, lineHeight: 1.7 }}>
            No problem — if you have a scale or tape measure nearby, taking 2 minutes to measure now will make your plan meaningfully more accurate. Otherwise, your coach will confirm your numbers with you before your plan is built.
          </p>
        </div>
      )}

      <SinglePill label="Accountability preference (optional)" value={form.clientProfile.accountabilityInterest} onChange={v => set("clientProfile","accountabilityInterest",v)} options={ACCT_INTEREST} />

      {/* System read preview */}
      <div style={{ background: "#0A0A07", border: `1px solid ${c.border}`, borderRadius: 12, padding: "16px 18px", marginTop: 8 }}>
        <p style={{ color: c.muted, fontSize: 9, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Plan Readiness</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <ConfBadge label="Plan Ready" active={flags.metricsReady} col={c.green} textCol={c.greenText} />
          <ConfBadge label="Advanced Track Available" active={flags.fatLossPaceEligible} col={c.yellow} textCol={c.yellowText} />
          {!isPublic && <ConfBadge label="Coach Review" active={flags.coachReviewRequired} col={c.red} textCol={c.redText} />}
          {!isPublic && (
            <div style={{ background: confidence === "high" ? c.green : confidence === "medium" ? c.yellow : c.red, borderRadius: 7, padding: "6px 12px" }}>
              <span style={{ color: confidence === "high" ? c.greenText : confidence === "medium" ? c.yellowText : c.redText, fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                {confidence.toUpperCase()} confidence
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── v4: SAFETY BRIDGE — replaces the plan preview when a hard stop fires.
// Warm, premium, never a rejection. The lead is captured; the path changes.
function SafetyBridge({ form, hardStops, onReset }) {
  const firstName = form.clientInfo.name ? form.clientInfo.name.split(" ")[0] : "there";
  const isYouth = hardStops.includes("under_18");
  const isPregnancy = hardStops.includes("pregnancy_deficit");
  const isMedical = hardStops.includes("chest_pain") || hardStops.includes("doctor_restriction");

  const headline = isYouth ? "Let's do this the right way."
    : isPregnancy ? "Your plan deserves extra care right now."
    : "Let's build this together — safely.";

  const body = isYouth
    ? "For anyone under 18, we build plans together with a parent or guardian involved. It's how we make sure everything is right for a growing body — and it's non-negotiable for us, because we take this seriously."
    : isPregnancy
    ? "Pregnancy and breastfeeding change what safe, effective nutrition looks like — a standard deficit plan isn't the right tool right now, and handing you one anyway would be careless. What we can do is build something designed for exactly where you are, ideally alongside your doctor. That's a conversation, not a form."
    : "Something you shared tells us your plan should start with a conversation, not an automated document. That's not a rejection — it's the opposite. It means you get Kevin or Yuny personally designing around your situation from day one, and we may ask you to loop in your doctor so everything we build is fully safe.";

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "60px 0 100px", textAlign: "center" }}>
      <div style={{ color: c.gold, fontSize: 9, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>KY Empowered Fitness</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 28 }}>
        <div style={{ flex: 1, maxWidth: 80, height: 1, background: `linear-gradient(90deg, transparent, ${c.goldDark})` }} />
        <span style={{ color: c.gold, fontSize: 18 }}>✦</span>
        <div style={{ flex: 1, maxWidth: 80, height: 1, background: `linear-gradient(90deg, ${c.goldDark}, transparent)` }} />
      </div>
      <h1 style={{ color: c.text, fontSize: "clamp(28px,5vw,42px)", margin: "0 0 18px", lineHeight: 1.15 }}>
        {headline}
      </h1>
      <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.85, margin: "0 auto 28px", maxWidth: 520 }}>
        Hey {firstName} — thank you for being honest with us. {body}
      </p>
      <div style={{ background: "#0C0C08", border: `1px solid ${c.goldDark}55`, borderRadius: 12, padding: "26px 28px", margin: "0 auto 24px", maxWidth: 480 }}>
        <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 10px" }}>Your next step</p>
        <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.7, margin: "0 0 20px" }}>
          Everything you just shared is already with Kevin and Yuny. Book a free call and they'll design your path personally{isYouth ? " — bring a parent or guardian along" : ""}.
        </p>
        <a href={CALENDLY_URL} target="_blank" rel="noreferrer" style={{
          display: "inline-block",
          background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`,
          border: 0, borderRadius: 8, padding: "16px 34px",
          color: c.black, fontSize: 12, fontWeight: 900, cursor: "pointer",
          letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", fontFamily: "Georgia, serif",
        }}>Book a Free Call</a>
      </div>
      <p style={{ color: c.mutedDark, fontSize: 12, lineHeight: 1.7, maxWidth: 440, margin: "0 auto 32px" }}>
        Based in Miami · In-person, home visits, and online worldwide
      </p>
      <button onClick={onReset} style={{
        background: "transparent", border: `1px solid ${c.border}`, borderRadius: 7,
        color: c.muted, fontSize: 10, cursor: "pointer", fontFamily: "Georgia, serif",
        letterSpacing: 2.5, textTransform: "uppercase", padding: "10px 22px",
      }}>
        Start a new intake
      </button>
    </div>
  );
}

// ── PLAN PREVIEW ─────────────────────────────────────────────────────────────
function PreviewCard({ num, title, children }) {
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: "22px 24px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
        <span style={{ color: c.goldDark, fontSize: 11, fontWeight: 700, letterSpacing: 2 }}>{num}</span>
        <h4 style={{ color: c.text, fontSize: 17, margin: 0, letterSpacing: 0.5 }}>{title}</h4>
      </div>
      {children}
    </div>
  );
}

function MetBox({ label, value, sub, accent }) {
  return (
    <div style={{ background: accent ? `${c.goldDark}22` : "#0A0A07", border: `1px solid ${accent ? c.goldDark : c.border}`, borderRadius: 10, padding: "16px" }}>
      <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 6px" }}>{label}</p>
      <p style={{ color: accent ? c.goldLight : c.text, fontSize: 26, fontWeight: 700, margin: "0 0 4px" }}>{value}</p>
      <p style={{ color: c.mutedDark, fontSize: 11, margin: 0 }}>{sub}</p>
    </div>
  );
}

function PlanPreview({ form, derived, flags, calcs, hardStops, onReset, isPublic = true }) {
  const [showJSON, setShowJSON] = useState(false);
  const [copied, setCopied] = useState(false);
  const confidence = computeConfidence(form, flags, calcs);
  const prev = generatePreview(form, derived, flags, calcs);
  const goalEmoji = { fat_loss: "🔥", muscle_gain: "💪", body_recomposition: "⚡", performance: "🏆", general_health: "🌿", maintenance: "⚖️" };
  const jsonStr = JSON.stringify(buildJSON(form, derived, flags, confidence, calcs, hardStops), null, 2);
  const copy = () => { navigator.clipboard.writeText(jsonStr).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  const goalLabel = GOALS.find(g => g.v === form.goalProfile.primaryGoal)?.l?.toLowerCase() || "your goal";

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 0 100px" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ color: c.gold, fontSize: 9, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>KY Empowered Fitness</div>
        <h1 style={{ color: c.text, fontSize: "clamp(28px,5vw,44px)", margin: "0 0 14px", lineHeight: 1.1 }}>Your KY Plan Preview</h1>
        <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.7, maxWidth: 500, margin: "0 0 20px" }}>
          Hey {prev.firstName} — here's what we're building for you.
        </p>
        <div style={{ display: "inline-block", background: "#0A0A07", border: `1px solid ${c.goldDark}55`, borderRadius: 8, padding: "10px 20px" }}>
          <span style={{ color: c.muted, fontSize: 12, letterSpacing: 0.5 }}>KY is not random workouts and diets. It's </span>
          <span style={{ color: c.goldLight, fontSize: 12, fontWeight: 700 }}>structure, accountability, and real change.</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <PreviewCard num="01" title="Your Plan at a Glance">
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>
            {goalEmoji[form.goalProfile.primaryGoal] || "✦"} We're building a <strong style={{ color: c.goldLight }}>{prev.paceStyle}</strong> approach focused on <strong style={{ color: c.goldLight }}>{prev.goalLabel}</strong>. Your lifestyle, food preferences, and behavioral patterns are already factored into every decision below.
          </p>
        </PreviewCard>

        <PreviewCard num="02" title="Why This Works For You">
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>
            {prev.frictionCount > 0
              ? `We identified ${prev.frictionCount} friction point${prev.frictionCount > 1 ? "s" : ""} in your profile — including ${prev.frictionLabels.join(" and ")}. Your plan is built around these realities, not in spite of them.`
              : "Your plan is built around what actually works for your life — not a generic template."}
            {prev.cuisineLabel ? ` Your ${prev.cuisineLabel} food preferences are integrated into the nutrition approach.` : ""}
          </p>
        </PreviewCard>

        {/* v4: gentle health acknowledgment — clients who disclosed see care, not silence */}
        {form.healthProfile.hasHealthFlags === "yes" && (
          <div style={{ background: "#0A0906", border: `1px solid ${c.goldDark}33`, borderLeft: `3px solid ${c.goldDark}`, borderRadius: "0 10px 10px 0", padding: "18px 20px" }}>
            <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 8px" }}>About what you shared</p>
            <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.85, margin: 0 }}>
              Thank you for telling us about your health context. Kevin or Yuny will personally review it before your full plan is finalized — everything gets built around your body, including the parts that need extra care.
            </p>
          </div>
        )}

        {/* Reality Anchor */}
        {prev.frictionCount > 0 && (
          <div style={{ background: "#0A0906", border: `1px solid ${c.goldDark}33`, borderLeft: `3px solid ${c.goldDark}`, borderRadius: "0 10px 10px 0", padding: "18px 20px" }}>
            <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 8px" }}>A note worth reading</p>
            <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.85, margin: 0 }}>
              If your weeks keep running the same way, your results won't change either. The numbers in this plan are only part of the equation.
              <span style={{ color: c.goldLight }}> The other part is what changes around the behavior — and that's where coaching comes in.</span>
            </p>
          </div>
        )}

        <PreviewCard num="03" title="Starting Targets">
          {prev.targetCals || prev.protein ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
                {prev.tdee && <MetBox label="Maintenance" value={prev.tdee.toLocaleString() + " cal"} sub="your estimated TDEE" />}
                {prev.targetCals && <MetBox label="Target Intake" value={prev.targetCals.toLocaleString() + " cal"} sub={`adjusted for ${goalLabel}`} accent />}
                {prev.protein && <MetBox label="Daily Protein" value={prev.protein + "g"} sub="minimum daily target" />}
              </div>
              {calcs.calorieFloorApplied && (
                <p style={{ color: c.yellowText, fontSize: 12.5, lineHeight: 1.7, margin: "12px 0 0" }}>
                  ⚠ Your inputs produced an unusually low starting number, so we've applied a safe minimum. Your coach will verify your measurements with you before finalizing.
                </p>
              )}
            </div>
          ) : (
            <p style={{ color: c.muted, fontSize: 14, margin: 0 }}>Complete your measurements to unlock calorie and macro targets.</p>
          )}
        </PreviewCard>

        <PreviewCard num="04" title="Your First Action">
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>
            <strong style={{ color: c.gold }}>Start here →</strong> {prev.firstAction}
          </p>
        </PreviewCard>

        {/* High-intent conversion strip */}
        <div style={{ background: "#0C0C08", border: `1px solid ${c.goldDark}55`, borderRadius: 12, padding: "22px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ color: c.muted, fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 6px" }}>This is where most people stop.</p>
            <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
              If you want help actually executing this — the training, the nutrition, the accountability — we've got you.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" style={{
              background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`,
              border: 0, borderRadius: 8, padding: "13px 22px",
              color: c.black, fontSize: 11, fontWeight: 900, cursor: "pointer",
              letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", fontFamily: "Georgia, serif",
              whiteSpace: "nowrap",
            }}>Book a Call</a>
            <a href="https://instagram.com/kyempoweredfitness" target="_blank" rel="noreferrer" style={{
              background: "transparent",
              border: `1px solid ${c.goldDark}`,
              borderRadius: 8, padding: "13px 22px",
              color: c.gold, fontSize: 11, fontWeight: 700, cursor: "pointer",
              letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", fontFamily: "Georgia, serif",
              whiteSpace: "nowrap",
            }}>DM on Instagram</a>
          </div>
        </div>

        <PreviewCard num="05" title="Training Alignment">
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>{prev.trainingNote}</p>
        </PreviewCard>

        <PreviewCard num="06" title="Adherence Prediction">
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>
            <strong style={{ color: c.goldLight }}>Prediction: </strong>{prev.adherencePred}
          </p>
        </PreviewCard>

        {form.goalProfile.primaryGoal === "fat_loss" && (
          <PreviewCard num="07" title="Fat Loss Pace Classification">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: flags.fatLossPaceEligible ? c.greenText : c.goldLight, flexShrink: 0, marginTop: 5 }} />
              <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: 0 }}>{prev.paceClass}</p>
            </div>
          </PreviewCard>
        )}

        {/* Soft-close bridge */}
        <p style={{ color: c.muted, fontSize: 15, lineHeight: 1.8, textAlign: "center", fontStyle: "italic", margin: "6px 0 0" }}>
          Most people don't need a better plan. They need someone to help them follow it.
        </p>

        {/* Bottom CTA */}
        <div style={{ background: "linear-gradient(135deg,#0A0A07,#111008)", border: `1px solid ${c.goldDark}44`, borderRadius: 14, padding: "36px 28px" }}>
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <div style={{ color: c.gold, fontSize: 9, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14, textAlign: "center" }}>Ready to Stop Restarting</div>
            <h3 style={{ color: c.text, fontSize: "clamp(22px,4vw,30px)", margin: "0 0 10px", lineHeight: 1.2, textAlign: "center" }}>
              This isn't a plan. This is where the plan gets executed.
            </h3>
            <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.7, textAlign: "center", margin: "0 0 24px" }}>
              Kevin and Yuneisy don't hand you a document and wish you luck. They coach you through every week.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: 28 }}>
              {[
                { icon: "✦", text: "Personalized training designed for your body and schedule" },
                { icon: "✦", text: "Nutrition coaching built around food you actually eat" },
                { icon: "✦", text: "Real accountability — weekly check-ins, not silence" },
                { icon: "✦", text: "Two certified coaches working as one team for you" },
              ].map((b, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ color: c.goldDark, fontSize: 12, marginTop: 2, flexShrink: 0 }}>{b.icon}</span>
                  <span style={{ color: c.soft, fontSize: 13, lineHeight: 1.65 }}>{b.text}</span>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer" style={{
                background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`,
                border: 0, borderRadius: 8, padding: "17px 36px",
                color: c.black, fontSize: 13, fontWeight: 900, cursor: "pointer",
                letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", fontFamily: "Georgia, serif",
              }}>Book a Free Call</a>
              <a href="https://instagram.com/kyempoweredfitness" target="_blank" rel="noreferrer" style={{
                background: "transparent", border: `1px solid ${c.goldDark}`,
                borderRadius: 8, padding: "13px 22px",
                color: c.muted, fontSize: 11, fontWeight: 700,
                letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", fontFamily: "Georgia, serif",
              }}>DM on Instagram</a>
            </div>

            <p style={{ color: c.mutedDark, fontSize: 11, textAlign: "center", marginTop: 16, letterSpacing: 0.5 }}>
              Based in Miami · In-person, home visits, and online worldwide
            </p>
          </div>
        </div>

        {/* Coach Export — internal only */}
        {!isPublic && (
        <div style={{ border: `1px solid ${c.border}`, borderRadius: 14, overflow: "hidden" }}>
          <button onClick={() => setShowJSON(!showJSON)} style={{
            width: "100%", background: "#0A0A07", border: 0,
            padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
            cursor: "pointer", fontFamily: "Georgia, serif",
          }}>
            <div style={{ textAlign: "left" }}>
              <span style={{ color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Coach Export</span>
              <span style={{ color: c.mutedDark, fontSize: 11, marginLeft: 12 }}>Internal — not visible to client</span>
            </div>
            <span style={{ color: c.muted, fontSize: 13 }}>{showJSON ? "▲" : "▼"}</span>
          </button>
          {showJSON && (
            <div>
              <div style={{ background: "#060604", borderTop: `1px solid ${c.border}`, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: c.muted, fontSize: 12 }}>KY System JSON — structured intake output</span>
                <button onClick={copy} style={{ background: copied ? c.green : `${c.goldDark}33`, border: `1px solid ${copied ? c.greenText : c.goldDark}`, borderRadius: 7, padding: "7px 16px", color: copied ? c.greenText : c.gold, cursor: "pointer", fontSize: 11, fontFamily: "Georgia, serif", letterSpacing: 1 }}>
                  {copied ? "✓ Copied" : "Copy JSON"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "16px 20px", color: "#B8A87A", fontSize: 11.5, lineHeight: 1.75, overflowX: "auto", fontFamily: "monospace", background: "#060604" }}>
                {jsonStr}
              </pre>
            </div>
          )}
        </div>
        )}

        <div style={{ textAlign: "center", paddingTop: 4, paddingBottom: 8 }}>
          <button onClick={onReset} style={{
            background: "transparent",
            border: `1px solid ${c.border}`,
            borderRadius: 7,
            color: c.muted,
            fontSize: 10,
            cursor: "pointer",
            fontFamily: "Georgia, serif",
            letterSpacing: 2.5,
            textTransform: "uppercase",
            padding: "10px 22px",
            transition: "border-color .2s, color .2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = c.goldDark; e.currentTarget.style.color = c.soft; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.muted; }}
          >
            Start a new intake
          </button>
        </div>

      </div>
    </div>
  );
}

// ── SUBMISSION ───────────────────────────────────────────────────────────────
const FORMSPREE_INTAKE = "https://formspree.io/f/xlgovboz";

async function submitIntake(form, derived, flags, prev, confidence, calcs, hardStops) {
  const fmtList = (arr) => arr && arr.length ? arr.join(", ") : "None";
  const fmtNum  = (n, suffix = "") => n ? `${n.toLocaleString()}${suffix}` : "—";

  const payload = {
    type: "KY Intake System",
    _replyto: form.clientInfo.email || "",
    submittedAt: new Date().toISOString(),
    submissionDate: new Date().toLocaleDateString(),

    name: form.clientInfo.name || "",
    email: form.clientInfo.email || "",
    phone: normalizePhone(form.clientInfo.phone),

    primaryGoal: form.goalProfile.primaryGoal || "",
    goalUrgency: form.goalProfile.goalUrgency || "",

    age: form.bodyData.age || "",
    sex: form.bodyData.sex || "",
    height: form.bodyData.height.value ? `${form.bodyData.height.value} ${form.bodyData.height.unit}` : "",
    heightNormalizedIn: calcs.heightIn ? Math.round(calcs.heightIn) : "",
    heightInterpretation: calcs.heightDisplay || "",
    weight: form.bodyData.weight.value ? `${form.bodyData.weight.value} ${form.bodyData.weight.unit}` : "",
    dataConfidence: form.bodyData.dataConfidence || "",

    // ── v4: Health screening ──────────────────────────────────────────────
    hasHealthFlags: form.healthProfile.hasHealthFlags || "",
    healthConditions: fmtList(form.healthProfile.conditions),
    injuriesNotes: form.healthProfile.injuriesNotes || "None",
    medicationsNotes: form.healthProfile.medicationsNotes || "None",
    chestPainDuringActivity: form.healthProfile.chestPain || "n/a",
    doctorAdvisedRestriction: form.healthProfile.doctorRestriction || "n/a",
    pregnancyStatus: form.healthProfile.pregnancyStatus || "n/a",
    hardStops: hardStops.length ? hardStops.join(", ") : "None", // v4

    activityLevel: form.goalProfile.activityLevel || "",
    dietHistory: form.goalProfile.dietHistory || "",
    lifestyleLoad: form.clientProfile.lifestyleLoad || "",
    energyRecovery: form.clientProfile.energyRecovery || "",
    consistencyScore: form.clientProfile.consistencyScore ?? "",
    accountabilityInterest: form.clientProfile.accountabilityInterest || "",

    trainingExperience: form.goalProfile.trainingExperience || "",
    availableDaysPerWeek: form.goalProfile.availableDaysPerWeek || "",
    trainingTimeOfDay: form.goalProfile.trainingTimeOfDay || "",

    adherenceRisk: derived.adherenceRisk || "",
    planIntensity: derived.planIntensityInput || "",
    primaryDriver: derived.primaryDriverInput || "",
    nutritionStrategy: derived.nutritionStrategyInput || "",

    frictionPoints: fmtList(form.frictionProfile.frictionPoints),
    criticalZone: form.frictionProfile.criticalZone || "",
    offTrackPattern: form.frictionProfile.offTrackPattern || "",
    socialEventPattern: form.frictionProfile.socialEventPattern || "",
    riskWindows: fmtList(derived.riskWindows),

    cuisine: form.nutritionContext.cuisinePreference || "",
    cookingLevel: form.nutritionContext.cookingLevel || "",
    mealPrepSupport: form.nutritionContext.mealPrepSupport || "",
    mealStructure: form.nutritionContext.mealStructurePreference || "",
    allergies: fmtList(form.nutritionContext.knownAllergies),
    foodsLiked: fmtList(form.nutritionContext.foodsLiked),
    foodsDisliked: fmtList(form.nutritionContext.foodsDisliked),
    weekdayPattern: form.nutritionContext.weekdayPattern || "",
    weekendPattern: form.nutritionContext.weekendPattern || "",

    targetCalories: fmtNum(calcs.targetCals, " cal"),
    rawTargetCalories: fmtNum(calcs.rawTarget, " cal"),
    calorieFloorApplied: calcs.calorieFloorApplied,
    maintenanceCalories: fmtNum(calcs.tdee, " cal"),
    proteinTarget: fmtNum(calcs.protein, "g"),
    fatLossPaceClass: prev.paceClass || "",
    adherencePrediction: prev.adherencePred || "",

    metricsReady: flags.metricsReady,
    fatLossPaceEligible: flags.fatLossPaceEligible,
    coachReviewRequired: flags.coachReviewRequired,
    systemConfidence: confidence,
    systemWarnings: calcs.warnings.length ? calcs.warnings.join(" • ") : "None",
    healthWarnings: flags.healthWarnings.length ? flags.healthWarnings.join(" • ") : "None", // v4

    source: "KY Website — /start (Intake System v4)",
  };

  const res = await fetch(FORMSPREE_INTAKE, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`[KY Intake] Formspree ${res.status}: ${body.slice(0, 200)}`);
  }
}

// ── DEFAULT FORM STATE ───────────────────────────────────────────────────────
const DEFAULT_FORM = {
  clientInfo: { name: "", email: "", phone: "", date: new Date().toISOString().slice(0, 10), coach: "" },
  bodyData: { age: "", sex: "", height: { value: "", unit: "in" }, weight: { value: "", unit: "lb" }, bodyFatPercent: "", dataConfidence: "" },
  // v4: healthProfile section added
  healthProfile: { hasHealthFlags: "", conditions: [], injuriesNotes: "", medicationsNotes: "", chestPain: "", doctorRestriction: "", pregnancyStatus: "" },
  goalProfile: { primaryGoal: "", goalUrgency: "", activityLevel: "", dietHistory: "", trainingExperience: "", availableDaysPerWeek: "", trainingTimeOfDay: "" },
  clientProfile: { adherenceRisk: "", planIntensityInput: "", lifestyleLoad: "", primaryDriverInput: "", nutritionStrategyInput: "", energyRecovery: "", consistencyScore: 5, accountabilityInterest: "" },
  frictionProfile: { frictionPoints: [], criticalZone: "", offTrackPattern: "", riskWindows: [], socialEventPattern: "" },
  nutritionContext: { cuisinePreference: "", cookingLevel: "", mealPrepSupport: "", mealStructurePreference: "", knownAllergies: [], foodsLiked: [], foodsDisliked: [], weekdayPattern: "", weekendPattern: "" },
};

const STEP_TITLES = ["Identity + Goal", "Lifestyle Reality", "Behavior Insight", "Nutrition Context", "Intent + Close"];
const TOTAL = 5;

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function KYIntakeSystem({ onBack, isPublic = true }: { onBack?: () => void; isPublic?: boolean } = {}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [mode, setMode] = useState("new");
  const [submitted, setSubmitted] = useState(false);
  const [fading, setFading] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  const set = useCallback((section, field, value) => {
    setForm(f => ({ ...f, [section]: { ...f[section], [field]: value } }));
  }, []);
  const setN = useCallback((section, field, sub, value) => {
    setForm(f => ({ ...f, [section]: { ...f[section], [field]: { ...f[section][field], [sub]: value } } }));
  }, []);

  const calcs = useMemo(() => computeCalcs(form), [form]);
  const derived = useMemo(() => deriveFields(form), [form]);
  const flags = useMemo(() => computeFlags(form, derived, calcs), [form, derived, calcs]);
  const hardStops = useMemo(() => computeHardStops(form, derived), [form, derived]); // v4
  const confidence = useMemo(() => computeConfidence(form, flags, calcs), [form, flags, calcs]);
  const completion = useMemo(() => computeCompletion(form), [form]);
  const canAdvance = isStepComplete(step, form, calcs);

  const goTo = (next) => {
    if (fading) return;
    setFading(true);
    setTimeout(() => { setStep(next); setFading(false); window.scrollTo({ top: 0, behavior: "smooth" }); }, 200);
  };

  const applyReturning = () => {
    setForm(f => ({
      ...f,
      bodyData: { ...f.bodyData, sex: RETURNING_TEMPLATE.sex },
      nutritionContext: {
        ...f.nutritionContext,
        knownAllergies: RETURNING_TEMPLATE.knownAllergies,
        cuisinePreference: RETURNING_TEMPLATE.cuisinePreference,
        foodsLiked: RETURNING_TEMPLATE.foodsLiked,
        foodsDisliked: RETURNING_TEMPLATE.foodsDisliked,
        cookingLevel: RETURNING_TEMPLATE.cookingLevel,
      },
    }));
  };

  const handleReset = () => { setForm(DEFAULT_FORM); setStep(0); setSubmitted(false); setMode("new"); setCoachOpen(false); };

  const handleSubmit = () => {
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const freshCalcs = computeCalcs(form);
    const freshStops = computeHardStops(form, derived); // v4
    const prev = generatePreview(form, derived, flags, freshCalcs);
    const conf = computeConfidence(form, flags, freshCalcs);
    // v4: hard-stop submissions still send — the lead + full flags reach the coaches
    submitIntake(form, derived, flags, prev, conf, freshCalcs, freshStops)
      .catch(err => console.error("[KY Intake] Submission error:", err));
  };

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "Georgia, serif", padding: "0 20px" }}>
        {/* ── v4: hard stops route to the SafetyBridge instead of the plan preview ── */}
        {hardStops.length > 0
          ? <SafetyBridge form={form} hardStops={hardStops} onReset={handleReset} />
          : <PlanPreview form={form} derived={derived} flags={flags} calcs={calcs} hardStops={hardStops} onReset={handleReset} isPublic={isPublic} />}
      </div>
    );
  }

  const confColor = confidence === "high" ? c.green : confidence === "medium" ? c.yellow : c.red;
  const confText = confidence === "high" ? c.greenText : confidence === "medium" ? c.yellowText : c.redText;

  // ── STEP 0: INTRO BRIDGE ─────────────────────────────────────────────────
  if (step === 0) {
    return (
      <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif", display: "flex", flexDirection: "column" }}>
        <style>{`@keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(139,104,32,.35)}50%{box-shadow:0 0 38px rgba(201,151,58,.52)}}`}</style>
        <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {onBack && (
              <button onClick={onBack} style={{ background: "transparent", border: "none", color: c.mutedDark, fontSize: 11, cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: 1, padding: 0 }}>
                ← Back
              </button>
            )}
            <div style={{ color: c.gold, fontSize: 9, letterSpacing: 4, textTransform: "uppercase" }}>KY Empowered Fitness</div>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 24px 80px" }}>
          <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 36 }}>
              <div style={{ flex: 1, maxWidth: 80, height: 1, background: `linear-gradient(90deg, transparent, ${c.goldDark})` }} />
              <span style={{ color: c.gold, fontSize: 18 }}>✦</span>
              <div style={{ flex: 1, maxWidth: 80, height: 1, background: `linear-gradient(90deg, ${c.goldDark}, transparent)` }} />
            </div>
            <p style={{ color: c.gold, fontSize: 10, letterSpacing: 6, textTransform: "uppercase", marginBottom: 20 }}>KY Transformation System</p>
            <h1 style={{ color: c.text, fontSize: "clamp(32px,6vw,56px)", lineHeight: 1.05, margin: "0 0 20px", letterSpacing: 0.5 }}>
              This is where your plan begins.
            </h1>
            <p style={{ color: c.soft, fontSize: "clamp(16px,2.5vw,20px)", lineHeight: 1.75, margin: "0 0 12px" }}>
              This takes about 2–3 minutes. We're going to build something that actually fits your life.
            </p>
            <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>
              No guesswork. No generic plans.
            </p>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.7, margin: "0 0 36px", letterSpacing: 0.3 }}>
              Kevin and Yuneisy will personally review what you submit.
            </p>
            <button
              onClick={() => { setStep(1); window.scrollTo({ top: 0 }); }}
              style={{
                background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`,
                border: 0, borderRadius: 10, padding: "18px 44px",
                color: c.black, fontSize: 13, fontWeight: 900, cursor: "pointer",
                letterSpacing: 3, textTransform: "uppercase", fontFamily: "Georgia, serif",
                animation: "glowPulse 3s ease-in-out infinite",
              }}
            >
              Let's Begin →
            </button>
            <p style={{ color: c.mutedDark, fontSize: 11, letterSpacing: 1.5, marginTop: 18, textTransform: "uppercase" }}>
              No commitment required
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <style>{`
        * { box-sizing: border-box; }
        input[type=number]::-webkit-inner-spin-button { opacity: 0.4; }
        input::placeholder, textarea::placeholder { color: #4A3E20; }
        select option { background: #0E0E0A; color: #F0E0C8; }
        input:focus, select:focus, textarea:focus { border-color: #8B6820 !important; box-shadow: 0 0 0 2px rgba(139,104,32,.12); }
      `}</style>

      {/* ── STICKY HEADER ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,8,6,.96)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${c.border}` }}>
        <div style={{ height: 3, background: "#181812" }}>
          <div style={{ height: "100%", width: `${(Math.max(step, 0) / TOTAL) * 100}%`, background: `linear-gradient(90deg,${c.goldDark},${c.gold})`, transition: "width .4s ease" }} />
        </div>

        <div style={{ maxWidth: 780, margin: "0 auto", padding: "11px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {onBack && (
              <button onClick={onBack} style={{ background: "transparent", border: "none", color: c.mutedDark, fontSize: 11, cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: 1, padding: 0, display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                ← Site
              </button>
            )}
            <div>
              <div style={{ color: c.gold, fontSize: 9, letterSpacing: 4, textTransform: "uppercase" }}>KY Empowered Fitness</div>
              <div style={{ color: c.muted, fontSize: 11, marginTop: 1 }}>Step {step} of {TOTAL} — {STEP_TITLES[step - 1]}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <div style={{ background: "#0A0A07", border: `1px solid ${c.border}`, borderRadius: 6, padding: "5px 10px" }}>
              <span style={{ color: c.gold, fontSize: 11, fontWeight: 700 }}>{completion}%</span>
              <span style={{ color: c.mutedDark, fontSize: 10, marginLeft: 4 }}>complete</span>
            </div>
            {!isPublic && (
              <div style={{ background: confColor, borderRadius: 6, padding: "5px 10px" }}>
                <span style={{ color: confText, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{confidence} conf.</span>
              </div>
            )}
            {!isPublic && flags.coachReviewRequired && (
              <button onClick={() => setCoachOpen(!coachOpen)} style={{ background: c.red, border: `1px solid ${c.redText}33`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontFamily: "Georgia, serif" }}>
                <span style={{ color: c.redText, fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>⚠ REVIEW</span>
              </button>
            )}
            {!isPublic && (
              <button onClick={() => setCoachOpen(!coachOpen)} style={{ background: coachOpen ? `${c.goldDark}22` : "transparent", border: `1px solid ${coachOpen ? c.goldDark : c.border}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: coachOpen ? c.gold : c.muted, fontFamily: "Georgia, serif", fontSize: 10, letterSpacing: 1 }}>
                ⚙ Coach
              </button>
            )}
          </div>
        </div>

        {/* Coach Panel — internal only */}
        {!isPublic && coachOpen && (
          <div style={{ borderTop: `1px solid ${c.border}`, background: "#070705", padding: "16px 20px" }}>
            <div style={{ maxWidth: 780, margin: "0 auto" }}>
              <p style={{ color: c.muted, fontSize: 9, letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>Coach Panel — Internal Only</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 12 }}>
                <SF label="Assigned Coach" value={form.clientInfo.coach} onChange={v => set("clientInfo","coach",v)} options={COACHES} />
                <FL label="Body Fat %">
                  <input type="number" value={form.bodyData.bodyFatPercent} onChange={e => set("bodyData","bodyFatPercent",e.target.value)} placeholder="e.g. 24" style={{ ...INP, fontSize: 13 }} />
                </FL>
              </div>
              {/* v4: hard stops shown to coach in-flow */}
              {hardStops.length > 0 && (
                <div style={{ background: c.red, border: `1px solid ${c.redText}55`, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                  <p style={{ color: c.redText, fontSize: 11, margin: 0, lineHeight: 1.7, fontWeight: 700 }}>
                    HARD STOP ACTIVE — plan generation will route to safety bridge: {hardStops.join(" • ")}
                  </p>
                </div>
              )}
              {flags.coachReviewRequired && (
                <div style={{ background: c.red, border: `1px solid ${c.redText}33`, borderRadius: 8, padding: "10px 14px" }}>
                  <p style={{ color: c.redText, fontSize: 11, margin: 0, lineHeight: 1.7 }}>
                    Review triggered by: {[
                      !flags.metricsReady && "incomplete core metrics",
                      form.bodyData.dataConfidence === "not_sure" && "data confidence = not sure",
                      (derived.planIntensityInput === "aggressive" && derived.adherenceRisk === "high") && "aggressive + high adherence risk",
                      isUnreasonableBF(form.bodyData.bodyFatPercent, form.bodyData.sex) && "body fat % out of expected range",
                      ...calcs.warnings,
                      ...flags.healthWarnings, // v4
                    ].filter(Boolean).join(" • ")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 20px 130px" }}>

        {step === 1 && !isPublic && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "inline-flex", border: `1px solid ${c.border}`, borderRadius: 10, overflow: "hidden" }}>
              {[{ v: "new", l: "New Client" }, { v: "returning", l: "Returning" }].map(m => (
                <button key={m.v} onClick={() => { setMode(m.v); if (m.v === "returning") applyReturning(); }} style={{
                  background: mode === m.v ? `${c.goldDark}33` : "transparent",
                  border: "none",
                  borderRight: m.v === "new" ? `1px solid ${c.border}` : "none",
                  color: mode === m.v ? c.goldLight : c.muted,
                  padding: "10px 22px",
                  cursor: "pointer",
                  fontFamily: "Georgia, serif",
                  fontSize: 11,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  transition: "all .2s",
                }}>{m.l}</button>
              ))}
            </div>
            {mode === "returning" && (
              <p style={{ color: c.muted, fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
                ✓ Previous intake pre-filled. Verify and update where needed — weight and goals reset intentionally.
              </p>
            )}
          </div>
        )}

        <div style={{ opacity: fading ? 0 : 1, transform: `translateY(${fading ? "6px" : "0"})`, transition: "opacity .2s, transform .2s" }}>
          {step === 1 && <Step1 form={form} set={set} setN={setN} />}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}
          {step === 4 && <Step4 form={form} set={set} />}
          {step === 5 && <Step5 form={form} set={set} flags={flags} confidence={confidence} isPublic={isPublic} />}
        </div>
      </div>

      {/* ── STICKY BOTTOM NAV ── */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(8,8,6,.97)", backdropFilter: "blur(12px)", borderTop: `1px solid ${c.border}`, padding: "14px 20px", zIndex: 100 }}>
        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {Array.from({ length: TOTAL }, (_, i) => (
              <div key={i} style={{ width: i + 1 === step ? 22 : 8, height: 8, borderRadius: 4, background: i + 1 < step ? c.goldDark : i + 1 === step ? c.gold : "#2A2410", transition: "all .3s ease" }} />
            ))}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            {step > 1 && (
              <button onClick={() => goTo(step - 1)} style={{ background: "transparent", border: `1px solid ${c.border}`, borderRadius: 8, padding: "12px 22px", color: c.muted, cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>
                ← Back
              </button>
            )}
            {step < TOTAL ? (
              <button onClick={() => canAdvance && goTo(step + 1)} disabled={!canAdvance} style={{
                background: canAdvance ? `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})` : `${c.goldDark}40`,
                border: "none", borderRadius: 8, padding: "12px 28px",
                color: canAdvance ? c.black : c.mutedDark,
                cursor: canAdvance ? "pointer" : "not-allowed",
                fontFamily: "Georgia, serif", fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase",
                transition: "all .2s",
              }}>
                Continue →
              </button>
            ) : (
              <button onClick={() => canAdvance && handleSubmit()} disabled={!canAdvance} style={{
                background: canAdvance ? `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})` : `${c.goldDark}40`,
                border: "none", borderRadius: 8, padding: "12px 28px",
                color: canAdvance ? c.black : c.mutedDark,
                cursor: canAdvance ? "pointer" : "not-allowed",
                fontFamily: "Georgia, serif", fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase",
              }}>
                Generate My Plan ✦
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
